// ============================================================================
// QUERY KEYS - House of Kairos OS
// ============================================================================
//
// Every React Query key in the app comes from this file. Keys are
// HIERARCHICAL: each entry is an array (or a function returning one), and a
// key nests under the key of the thing that owns it. TanStack matches
// invalidations by prefix, so invalidating a parent refreshes everything under
// it — one call instead of an enumerated list.
//
//   MEMBER_QUERY_KEYS.SCOPE(id)        ['member', id]
//     BY_ID(id)                        ['member', id, 'record']
//     NOTES(id)                        ['member', id, 'notes']
//     CLASS_BOOKING_QUERY_KEYS.BY_MEMBER(id)  ['member', id, 'class-bookings']
//     …                                every per-member tab on the profile
//
//   MEMBERSHIP_QUERY_KEYS.ALL          ['memberships']   (the whole family)
//     LIST                             ['memberships', 'list']
//     BY_MEMBER(memberId)              ['memberships', 'by-member', memberId]
//     BY_ID(id)                        ['memberships', 'detail', id]
//       FREEZES(id), NOTES(id), …      ['memberships', 'detail', id, …]
//
// Rules:
//
// - Every value is an array. Use it as-is (`queryKey: X.KEY`) or extend it
//   by spreading (`queryKey: [...X.KEY, filters]`). Never wrap it in another
//   array — `[X.KEY]` builds `[['…']]`, which TypeScript accepts and which
//   matches nothing. The reconciliation test rejects it.
// - A group's root is `ALL`. A sub-family that is invalidated on its own gets
//   an array root named `<THING>_ALL` next to the factory that extends it.
// - A key lives where its invalidations come from. Per-member views whose
//   writers always know the member sit in the member SCOPE. Collections whose
//   writers may only know the row id (a membership updated by id) sit under
//   their own group root instead.
// - Record keys (`BY_ID`) are leaves beside the scope, not the scope itself,
//   so a realtime "row changed" refresh re-reads only the row and not every
//   tab hanging off it.
//
// See src/lib/query-invalidation.ts for the family helpers that mutations
// call, and src/constants/__tests__/query-keys-reconciliation.test.ts for the
// check that every invalidated key has a query behind it.

// ============================================================================
// MEMBERS
// ============================================================================

export const MEMBER_QUERY_KEYS = {
  /**
   * Every member list: the paged list, the pickers, the directory and the
   * deleted list. Member mutations invalidate this to refresh all of them.
   */
  ALL: ['members'] as const,
  /**
   * Slim non-deleted roster for pickers and the header search
   * (`useMemberDirectory`). Under ALL so every member mutation refreshes it.
   */
  DIRECTORY: ['members', 'directory'] as const,
  DELETED: ['members', 'deleted'] as const,
  REPORT: ['members-report'] as const,

  /** Everything the Member Profile shows for one member. */
  SCOPE: (memberId: string) => ['member', memberId] as const,
  /** The member row itself. A leaf, so a row refresh stays a row refresh. */
  BY_ID: (memberId: string) => ['member', memberId, 'record'] as const,
  PRIVACY_SETTINGS: (memberId: string) => ['member', memberId, 'privacy-settings'] as const,
  DELETION_IMPACT: (memberId: string) => ['member', memberId, 'deletion-impact'] as const,
  CHECK_INS: (memberId: string) => ['member', memberId, 'check-ins'] as const,
  NOTES: (memberId: string) => ['member', memberId, 'notes'] as const,
  /** Class booking strikes + the current pause (profile → Strikes tab). */
  STRIKES: (memberId: string) => ['member', memberId, 'strikes'] as const,
  ACTIVITY_LOGS: (memberId: string) => ['member', memberId, 'activity-logs'] as const,
  MOBILE_INFO: (memberId: string) => ['member', memberId, 'mobile-info'] as const,
  MOBILE_ACTIVITY: (memberId: string) => ['member', memberId, 'mobile-activity'] as const,
  PUSH_TOKENS: (memberId: string) => ['member', memberId, 'push-tokens'] as const,
  AUTH_INFO: (memberId: string | null | undefined) => ['member', memberId, 'auth-info'] as const,
  FILES: (memberId: string) => ['member', memberId, 'files'] as const,
  LIFETIME_SPEND: (memberId: string | null | undefined) => ['member', memberId, 'lifetime-spend'] as const,
  WAIVER_THANK_YOU: (memberId: string | null | undefined) => ['member', memberId, 'waiver-thank-you'] as const,

  /** Keyed by the auth user, not the member — the caller only has that id. */
  AUTH_DETAILS: (authUserId: string | null | undefined) =>
    ['member-auth-details', authUserId] as const,
  /** Name lookups for change-log rendering. */
  DETAILS_FOR_LOGS: ['member-details-for-logs'] as const,
  CLASS_CHANGE_LOG_DETAILS: (classId: string, memberIds: readonly string[]) =>
    ['member-details-for-logs', 'class-change-log', classId, memberIds] as const,
} as const;

/**
 * Member feedback (mobile Home tile → member_feedback).
 *
 * Both keys share the ALL root so one invalidation refreshes the profile tab
 * and the Feedback page together.
 */
export const MEMBER_FEEDBACK_QUERY_KEYS = {
  ALL: ['member-feedback'] as const,
  /** Profile tab: one member, newest first. */
  BY_MEMBER: (memberId: string) => ['member-feedback', 'member', memberId] as const,
  /** Feedback page: all members, one page of FEEDBACK_PAGE_SIZE (1-based). */
  PAGE: (page: number) => ['member-feedback', 'page', page] as const,
} as const;

// ============================================================================
// MEMBERSHIPS
// ============================================================================

export const MEMBERSHIP_QUERY_KEYS = {
  /**
   * The whole membership family. Anything that writes a membership
   * invalidates this (see invalidateMembershipFamily): lists, per-member and
   * bulk lookups, every detail and everything nested under a detail.
   */
  ALL: ['memberships'] as const,
  LIST: ['memberships', 'list'] as const,
  BY_MEMBER: (memberId: string) => ['memberships', 'by-member', memberId] as const,
  BULK_BY_MEMBERS: (memberIds: readonly string[]) =>
    ['memberships', 'bulk-by-members', memberIds] as const,
  FREEZE_SUMMARY: (membershipIds: readonly string[]) =>
    ['memberships', 'freeze-summary', membershipIds] as const,
  REFUNDS_UNSETTLED: ['memberships', 'refunds-unsettled'] as const,
  TRANSFER_DESTINATION_CHECK: (memberId: string | null | undefined) =>
    ['memberships', 'transfer-destination-check', memberId] as const,
  TRANSFERRED_AWAY_BY_MEMBER: (memberId: string) =>
    ['memberships', 'transferred-away', memberId] as const,
  TRANSFERRED_IN_BY_MEMBER: (memberId: string) =>
    ['memberships', 'transferred-in', memberId] as const,

  // One membership and the screens hanging off it.
  BY_ID: (id: string) => ['memberships', 'detail', id] as const,
  RECEIPTS: (id: string) => ['memberships', 'detail', id, 'receipts'] as const,
  FREEZES: (id: string) => ['memberships', 'detail', id, 'freezes'] as const,
  // Bookings a freeze change would cancel (the freeze dialogs' warning).
  FREEZE_IMPACT: (memberId: string, membershipId: string, change: unknown) =>
    ['memberships', 'freeze-impact', memberId, membershipId, change] as const,
  ACTIVITY_LOGS: (id: string) => ['memberships', 'detail', id, 'activity-logs'] as const,
  NOTES: (id: string) => ['memberships', 'detail', id, 'notes'] as const,
  TRANSFERS: (id: string) => ['memberships', 'detail', id, 'transfers'] as const,
  REFUNDS: (id: string) => ['memberships', 'detail', id, 'refunds'] as const,
  REFUND_CONTEXT: (id: string) => ['memberships', 'detail', id, 'refund-context'] as const,

  // Reports: their own roots, refreshed explicitly where they matter.
  ACTIVE_MEMBER_GROWTH: ['active-member-growth'] as const,
  RENEWALS_REPORT: ['renewals-report'] as const,
} as const;

export const MEMBERSHIP_TYPE_QUERY_KEYS = {
  ALL: ['membership-types'] as const,
  BY_ID: (id: string) => ['membership-types', 'detail', id] as const,
} as const;

// ============================================================================
// CLASSES & BOOKINGS
// ============================================================================

export const CLASS_QUERY_KEYS = {
  /**
   * Every class list. booked_count / waitlist_count are derived by the list
   * query, so refreshing the capacity badges means invalidating this.
   */
  ALL: ['classes'] as const,
  BY_COACH: (coachId: string) => ['classes', 'coach', coachId] as const,
  /**
   * Today's schedule for the Dashboard "Classes Today" card, keyed by Bali
   * date so a tab left open across midnight asks for the new day. Under ALL,
   * so every booking, waitlist and cancellation write already refreshes it
   * through invalidateClassFamily / invalidateBookingFamily.
   */
  TODAY: (dateKey: string) => ['classes', 'today', dateKey] as const,
  ATTENDANCE_MEMBERSHIP_SPLIT: (classes: readonly { classId: string; classDate: string }[]) =>
    ['classes', 'attendance-membership-split', classes] as const,

  /** Every per-class scope, for writes that can touch any class (member delete). */
  SCOPES_ALL: ['class'] as const,
  /** Everything the class detail screen shows: record, roster, waitlist, logs. */
  SCOPE: (id: string) => ['class', id] as const,
  BY_ID: (id: string) => ['class', id, 'record'] as const,
  ACTIVITY_LOGS: (id: string) => ['class', id, 'activity-logs'] as const,
  BOOKING_EVENTS: (id: string) => ['class', id, 'activity-logs', 'booking-events'] as const,
  BOOKING_HISTORY: (id: string) => ['class', id, 'booking-history'] as const,
  // Who was booked / waitlisted when the class was (last) cancelled — the
  // class_cancellation_notices rows cancel_class wrote.
  CANCELLATION_ROSTER: (id: string) => ['class', id, 'cancellation-roster'] as const,
} as const;

export const CLASS_BOOKING_QUERY_KEYS = {
  BY_CLASS: (classId: string) => ['class', classId, 'bookings'] as const,
  BY_CLASS_AND_MEMBER: (classId: string, memberId: string) =>
    ['class', classId, 'bookings', 'member', memberId] as const,
  BY_MEMBER: (memberId: string) => ['member', memberId, 'class-bookings'] as const,

  // Does this member already hold a place in a class at the same time?
  // Booking or waitlisting anyone changes the answer for every class they
  // could clash with, not just this one — so a booking write invalidates the
  // whole CONFLICTS_ALL family. Without it the 5-minute staleTime can show
  // "no clash" for a member reception booked into the overlapping class a
  // moment ago.
  CONFLICTS_ALL: ['member-class-conflict'] as const,
  CONFLICT: (classId: string, memberId: string) =>
    ['member-class-conflict', classId, memberId] as const,
} as const;

// Staff guides fetched from the admin-only /api/help-guide route; one entry
// per slug, nothing to invalidate (the HTML changes only with a deploy).
export const HELP_GUIDE_QUERY_KEYS = {
  BY_SLUG: (slug: string) => ['help-guide', slug] as const,
} as const;

export const WAITLIST_QUERY_KEYS = {
  // There is no waitlist count key: waitlist_count is derived by the classes
  // list query (CLASS_QUERY_KEYS.ALL). One existed and was invalidated from
  // six call sites, none of which did anything. Invalidate CLASS_QUERY_KEYS.ALL.
  BY_CLASS: (classId: string) => ['class', classId, 'waitlist'] as const,
  MEMBER_CHECK: (classId: string, memberId: string | null | undefined) =>
    ['class', classId, 'waitlist', 'member-check', memberId] as const,
} as const;

export const CLASS_CATEGORY_QUERY_KEYS = {
  ALL: ['class-categories'] as const,
  BY_ID: (id: string) => ['class-categories', 'detail', id] as const,
  FOR_CHANGELOG: ['categories-for-changelog'] as const,
} as const;

export const CLASS_LOCATION_QUERY_KEYS = {
  ALL: ['class-locations'] as const,
  BY_ID: (id: string) => ['class-locations', 'detail', id] as const,
} as const;

export const COACH_QUERY_KEYS = {
  ALL: ['coaches'] as const,
  WITH_STATS: ['coaches', 'with-stats'] as const,
  BY_ID: (id: string) => ['coaches', 'detail', id] as const,
  FOR_CHANGELOG: ['coaches-for-changelog'] as const,
  /**
   * The member a coach is linked to. Its own root rather than a member key: it
   * holds a partial member row, and writing that into the member-detail cache
   * would leave the member page reading a row with most of its fields
   * missing. Linking/unlinking changes `coaches.member_id`, which changes this
   * key, so it needs no invalidation.
   */
  LINKED_MEMBER: (memberId: string | null | undefined) =>
    ['coach-linked-member', memberId] as const,
} as const;

/** Bookable resources (call booths, the library). */
export const BOOKABLE_RESOURCE_QUERY_KEYS = {
  ALL: ['bookable-resources'] as const,
  BY_ID: (id: string) => ['bookable-resources', 'detail', id] as const,
  PUBLIC_AVAILABILITY: (baliDateKey: string) =>
    ['public-booth-availability', baliDateKey] as const,
} as const;

export const RESOURCE_BOOKING_QUERY_KEYS = {
  /** Every by-day grid. */
  ALL: ['resource-bookings'] as const,
  // All bookings for a set of resources on a Bali calendar day. The ids are
  // sorted into one stable segment so key identity doesn't depend on order.
  BY_DAY: (resourceIds: readonly string[], baliDateKey: string) =>
    ['resource-bookings', baliDateKey, [...resourceIds].sort().join(',')] as const,
  BY_MEMBER: (memberId: string) => ['member', memberId, 'resource-bookings'] as const,
} as const;

// ============================================================================
// LOCKERS, PT, GIFT CARDS, FRIEND PASSES
// ============================================================================

export const LOCKER_QUERY_KEYS = {
  // Static locker config (124 rows, seeded by migration)
  ALL: ['lockers'] as const,
  /** Every assignment view: open assignments, history pages, usage stats. */
  ASSIGNMENTS_ALL: ['locker-assignments'] as const,
  // Open assignments only (returned_at IS NULL) with member + locker joins
  ACTIVE_ASSIGNMENTS: ['locker-assignments', 'active'] as const,
  // get_locker_usage_stats() RPC (one row per locker)
  USAGE_STATS: ['locker-assignments', 'usage-stats'] as const,
  HISTORY: (page: number, room: string, pageSize = 25, sort = 'latest', label = '') =>
    ['locker-assignments', 'history', room, page, pageSize, sort, label] as const,
  BY_MEMBER: (memberId: string) => ['member', memberId, 'locker-assignments'] as const,
} as const;

export const PT_SESSION_QUERY_KEYS = {
  /** Every PT session list, the ledger, details and per-pack counts. */
  ALL: ['pt-sessions'] as const,
  LEDGER: ['pt-sessions', 'ledger'] as const,
  BY_ID: (id: string) => ['pt-sessions', 'detail', id] as const,
  BY_COACH: (coachId: string) => ['pt-sessions', 'by-coach', coachId] as const,
  ACTIVE_COUNT: (packId: string) => ['pt-sessions', 'active-count', packId] as const,
  BY_MEMBER: (memberId: string) => ['member', memberId, 'pt-sessions'] as const,
} as const;

export const PT_SESSION_PACK_QUERY_KEYS = {
  ALL: ['pt-session-packs'] as const,
  BY_ID: (id: string) => ['pt-session-packs', 'detail', id] as const,
  BY_MEMBER: (memberId: string) => ['member', memberId, 'pt-session-packs'] as const,
} as const;

export const PT_SESSION_PACK_TYPE_QUERY_KEYS = {
  ALL: ['pt-session-pack-types'] as const,
  BY_ID: (id: string) => ['pt-session-pack-types', 'detail', id] as const,
} as const;

export const GIFT_CARD_QUERY_KEYS = {
  /** The list, every detail, redemptions and code lookups. */
  ALL: ['gift-cards'] as const,
  BY_ID: (id: string) => ['gift-cards', 'detail', id] as const,
  REDEMPTIONS: (id: string) => ['gift-cards', 'detail', id, 'redemptions'] as const,
  BY_CODE: (code: string) => ['gift-cards', 'by-code', code] as const,
} as const;

export const FRIEND_PASS_QUERY_KEYS = {
  BY_MEMBER: (memberId: string) => ['member', memberId, 'friend-passes'] as const,
} as const;

// ============================================================================
// CHECK-INS
// ============================================================================

export const CHECK_IN_QUERY_KEYS = {
  /**
   * Every check-in list and counter. A check-in write invalidates this one
   * key; the member-scoped history lives in MEMBER_QUERY_KEYS.CHECK_INS.
   */
  ALL: ['check-ins'] as const,
  IN_THE_HOUSE: ['check-ins', 'in-the-house'] as const,
  LAST_30_DAYS: ['check-ins', 'last-30-days'] as const,
  TODAY: ['check-ins', 'today'] as const,
  DATA: ['check-ins', 'data'] as const,
} as const;

// ============================================================================
// ADMINS & ACTIVITY LOG
// ============================================================================

export const ADMIN_USER_QUERY_KEYS = {
  /** The admin list and every admin detail. */
  ALL: ['admin-users'] as const,
  // id is optional because useAdminUser is called with an undefined route
  // param and gates the fetch on `enabled: Boolean(id)` — the key is still built.
  BY_ID: (id: string | undefined) => ['admin-users', 'detail', id] as const,
  /** Per-user permission grants for one admin (the editor on the admin view page). */
  GRANTS: (id: string | undefined) => ['admin-users', 'detail', id, 'grants'] as const,
  CURRENT: ['current-admin-user'] as const,
  CURRENT_PERMISSIONS: ['current-user-permissions'] as const,
  VAULTED_AVATARS_ALL: ['vaulted-admin-avatars'] as const,
  VAULTED_AVATARS: (sortedIds: readonly string[]) =>
    ['vaulted-admin-avatars', sortedIds] as const,
} as const;

export const ACTIVITY_LOG_QUERY_KEYS = {
  ALL: ['activity-logs'] as const,
  LIST: (options?: Record<string, unknown>) => ['activity-logs', 'list', options] as const,
  FILTER_OPTIONS: ['activity-logs', 'distinct-filter-values'] as const,
} as const;

// ============================================================================
// PHOTOS & STORAGE
// ============================================================================

/**
 * Member photos, kind first so each kind is one prefix.
 *
 * Two roots on purpose. ALL (webcam + admin-uploaded photos) is exempt from
 * the tab-focus refresh — re-signing those URLs on every tab switch is waste.
 * The 'member-profile-photos' family (photos the member sets from the app) is
 * not: a member can change it at any moment, and reception should see that on
 * return.
 */
export const MEMBER_PHOTO_QUERY_KEYS = {
  ALL: ['member-photos'] as const,
  BY_MEMBER: (memberId: string) => ['member-photos', 'photos', memberId] as const,
  UPLOADED_BY_MEMBER: (memberId: string) => ['member-photos', 'uploaded', memberId] as const,
  WEBCAM_BY_MEMBER: (memberId: string) => ['member-photos', 'webcam', memberId] as const,
  ALL_WEBCAM_BY_MEMBER: (memberId: string) => ['member-photos', 'webcam-all', memberId] as const,
  BULK_WEBCAM_ALL: ['member-photos', 'bulk-webcam'] as const,
  BULK_WEBCAM: (memberIds: readonly string[]) =>
    ['member-photos', 'bulk-webcam', memberIds] as const,

  ALL_PROFILE_BY_MEMBER: (memberId: string) =>
    ['member-profile-photos', 'all', memberId] as const,
  BULK_PROFILE_ALL: ['member-profile-photos', 'bulk'] as const,
  BULK_PROFILE: (memberIds: readonly string[]) =>
    ['member-profile-photos', 'bulk', memberIds] as const,
} as const;

export const STORAGE_QUERY_KEYS = {
  ALL: ['storage'] as const,
  STATS: ['storage', 'stats'] as const,
  DETAILED_STATS: ['storage', 'detailed-stats'] as const,
  BY_MEMBER: (memberId: string) => ['storage', 'member', memberId] as const,
} as const;

// ============================================================================
// DASHBOARD, FINANCE, REPORTS
// ============================================================================

export const DASHBOARD_QUERY_KEYS = {
  // Count queries
  MEMBERS_COUNT: ['members-count'] as const,
  CLASSES_COUNT: ['classes-count'] as const,
  TODAY_CLASSES_COUNT: ['today-classes-count'] as const,
  ACTIVE_MEMBERSHIPS_COUNT: ['active-memberships-count'] as const,
  TODAY_CHECK_INS_COUNT: ['today-check-ins-count'] as const,
  ACTIVE_MEMBERSHIPS_VALUE: ['active-memberships-value'] as const,

  // Chart queries
  LATEST_CHECK_INS: ['latest-check-ins'] as const,
  LATEST_MEMBERSHIPS: ['latest-memberships'] as const,
  TODAY_FNB_REVENUE: ['today-fnb-revenue'] as const,
  YESTERDAY_FNB_REVENUE: ['yesterday-fnb-revenue'] as const,
  YESTERDAY_MEMBERSHIP_REVENUE: ['yesterday-membership-revenue'] as const,
} as const;

export const FINANCE_QUERY_KEYS = {
  /** Root of the `['finance', <chart>, ...]` family, including the ledger. */
  ALL: ['finance'] as const,
  REFUND_SUMMARY: ['finance-refund-summary'] as const,
} as const;

/**
 * Report-screen query keys. ALL is the shared root of the
 * `['reports', <report>, ...filters]` family, which varies in arity.
 */
export const REPORT_QUERY_KEYS = {
  ALL: ['reports'] as const,
  AGE: ['age-report'] as const,
  CLASSES: ['classes-report'] as const,
  CLASS_MEMBERS: ['class-members-report'] as const,
  CLASS_STRIKES: ['class-strikes-report'] as const,
  CHECK_INS_BY_HOUR: ['reports-check-ins-by-hour'] as const,
  CHECK_INS_COMPETITOR_GATE: ['reports-check-ins-competitor-gate'] as const,
  CHECK_INS_DAILY_COUNTS: ['reports-check-ins-daily-counts'] as const,
  CHECK_INS_IN_HOUSE: ['reports-check-ins-in-house'] as const,
  CHECK_INS_LAST_30_DAYS: ['reports-check-ins-last-30-days'] as const,
  CHECK_INS_TODAY: ['reports-check-ins-today'] as const,
  FREEZE: ['freeze-report'] as const,
  GENDER: ['gender-report'] as const,
  MEMBERSHIPS: ['memberships-report'] as const,
  MEMBERSHIP_COHORTS: ['membership-cohorts-report'] as const,
  MEMBERSHIP_SPLIT: ['membership-split-report'] as const,
  MEMBER_CREATION: ['member-creation-report'] as const,
  MEMBER_TYPES: ['member-types-report'] as const,
  MOBILE_APP: ['mobile-app-report'] as const,
  PROFILE_PICTURES: ['profile-pictures-report'] as const,
  MRR: ['mrr-report'] as const,
  NATIONALITY: ['nationality-report'] as const,
  PT_SESSIONS: ['pt-sessions-report'] as const,
  PUSH_COVERAGE: ['push-coverage-report'] as const,
  PUSH_ROSTER: ['push-roster-report'] as const,
  REFUNDS: ['refunds-report'] as const,
  TOP_CHECK_INS_BY_MEMBERS: ['reports-top-check-ins-by-members'] as const,
  TOTAL_CHECK_INS: ['reports-total-check-ins'] as const,
  WALLET_PASSES: ['wallet-passes-report'] as const,
  CALENDAR_SYNC: ['calendar-sync-report'] as const,
  MOBILE_PERMISSIONS: ['mobile-permissions-report'] as const,
  EMAILS: ['emails-report'] as const,
  EMAIL_COVERAGE: ['email-coverage-report'] as const,
} as const;

// ============================================================================
// EVERYTHING ELSE
// ============================================================================

/**
 * Employee-related query keys.
 *
 * Moved here from a module-private `QUERY_KEYS` in useEmployees.ts. A local
 * factory is invisible to the reconciliation test, which compares the keys
 * queries register against the keys mutations invalidate. Keys have to live
 * in one place for that comparison to mean anything.
 */
export const EMPLOYEE_QUERY_KEYS = {
  ALL: ['employees'] as const,
  BY_ID: (id: string) => ['employees', 'detail', id] as const,
  // Odoo POS ledger for one employee (super_admin-scoped)
  PURCHASES: (id: string) => ['employee-odoo-purchases', id] as const,
  // End of the last successful staff purchase sync run (super_admin-scoped)
  PURCHASE_LAST_SYNC: ['employee-odoo-purchases', 'last-sync'] as const,
} as const;

/** Odoo apparel/merch stock snapshot. */
export const INVENTORY_QUERY_KEYS = {
  ALL: ['inventory'] as const,
} as const;

export const BPA_QUERY_KEYS = {
  ALL: ['bpa'] as const,
  BY_ID: (id: string) => ['bpa', 'detail', id] as const,
} as const;

export const NEWS_POST_QUERY_KEYS = {
  ALL: ['news-posts'] as const,
  BY_ID: (id: string) => ['news-posts', 'detail', id] as const,
} as const;

/**
 * Push notification campaigns. The audience preview is keyed on the segment
 * itself so two composers with the same filter share one count.
 */
export const PUSH_CAMPAIGN_QUERY_KEYS = {
  ALL: ['push-campaigns'] as const,
  BY_ID: (id: string) => ['push-campaigns', 'detail', id] as const,
  RECIPIENTS: (id: string) => ['push-campaigns', 'detail', id, 'recipients'] as const,
  AUDIENCE_PREVIEW: (audience: object) => ['push-audience-preview', audience] as const,
  AUDIENCE_MEMBERS: (audience: object) => ['push-audience-members', audience] as const,
} as const;

/** Odoo sync + POS ledger query keys. */
export const ODOO_QUERY_KEYS = {
  /** Root of the `['odoo', <panel>, ...]` family on the Odoo page. */
  ALL: ['odoo'] as const,
  PURCHASES: (memberId: string) => ['odoo-purchases', memberId] as const,
} as const;

// ============================================================================
// TAB-FOCUS REFRESH
// ============================================================================

/**
 * Query families the tab-focus refresh leaves alone (see AuthProvider).
 *
 * Returning to the tab refetches every active query so reception sees current
 * data, but these either change only through an admin's own edits (which
 * invalidate them directly) or are expensive to re-run for nothing: the member
 * directory is a ~3,000-row paged read, and the photo/avatar families re-sign
 * storage URLs that are valid for hours. Permissions are deliberately NOT here
 * — a grant revoked while the tab was hidden must apply on return.
 *
 * Each entry is a key prefix: a query is exempt when its key starts with it.
 */
export const FOCUS_REFRESH_EXEMPT_KEYS: readonly (readonly unknown[])[] = [
  MEMBER_QUERY_KEYS.DIRECTORY,
  MEMBERSHIP_TYPE_QUERY_KEYS.ALL,
  CLASS_CATEGORY_QUERY_KEYS.ALL,
  CLASS_LOCATION_QUERY_KEYS.ALL,
  BOOKABLE_RESOURCE_QUERY_KEYS.ALL,
  MEMBER_PHOTO_QUERY_KEYS.ALL,
  ADMIN_USER_QUERY_KEYS.VAULTED_AVATARS_ALL,
];

/** True when `queryKey` belongs to a family in FOCUS_REFRESH_EXEMPT_KEYS. */
export function isFocusRefreshExempt(queryKey: readonly unknown[]): boolean {
  return FOCUS_REFRESH_EXEMPT_KEYS.some(
    (prefix) =>
      prefix.length <= queryKey.length && prefix.every((part, i) => queryKey[i] === part),
  );
}
